/**
 * Nepal income tax calculation per IRD slabs.
 *
 * Uses the Finance Act slab table for the requested fiscal year (default
 * 2081/82). Supports single / couple status, SSF members (skip 1% SST),
 * non-resident flat 25%, women rebate (10% off tax), disabled/pensioner
 * exemptions, and remote-area allowances.
 *
 * @example
 * calculateIncomeTax({ income: 1_000_000, status: "single" });
 * // { fiscalYear: "2081/82", grossIncome: 1000000, taxableIncome: 1000000,
 * //   tax: 85000, effectiveRate: 0.085, perBracket: [...], notes: [...] }
 */

import { getRates } from "./data/index.js";
import type {
  FiscalYear,
  IncomeTaxResult,
  SlabSet,
  TaxProfile,
  TaxSlab,
} from "./types.js";
import { ensureNonNegative, r2, r4 } from "./util.js";

const REMOTE_AREA_DEDUCTIONS: Record<NonNullable<TaxProfile["remoteArea"]>, number> = {
  A: 50_000,
  B: 40_000,
  C: 30_000,
  D: 20_000,
  E: 10_000,
};

/** Pure function: apply a slab table to a taxable income amount. */
export function applySlabs(taxableIncome: number, slabs: SlabSet): {
  tax: number;
  perBracket: IncomeTaxResult["perBracket"];
} {
  ensureNonNegative(taxableIncome, "applySlabs");
  const perBracket: Array<{ bracket: TaxSlab; amountInBracket: number; tax: number }> = [];
  let tax = 0;
  let remaining = taxableIncome;
  for (const slab of slabs) {
    if (remaining <= 0) break;
    const span = slab.to - slab.from;
    const inBracket = Math.min(remaining, span);
    const slabTax = inBracket * slab.rate;
    perBracket.push({ bracket: slab, amountInBracket: r2(inBracket), tax: r2(slabTax) });
    tax += slabTax;
    remaining -= inBracket;
  }
  return { tax: r2(tax), perBracket };
}

/** Compute the basic exemption threshold given a profile & FY. */
function thresholdFor(slabs: SlabSet, profile: TaxProfile): number {
  // First slab end is the basic threshold.
  let base = slabs[0]?.to ?? 0;
  if (profile.isDisabled) base += 0.5 * base;
  if (profile.isPensioner) base += 0.25 * base;
  return base;
}

/** Income tax options. */
export interface IncomeTaxOptions {
  /** Annual gross income (NPR rupees). */
  readonly income: number;
  /** Filing status — defaults to `"single"`. */
  readonly status?: TaxProfile["status"];
  /** Fiscal year — defaults to current. */
  readonly fiscalYear?: FiscalYear;
  /** Tax-payer profile flags. */
  readonly profile?: Omit<TaxProfile, "status">;
  /** Pre-calculated total deductions to subtract from gross. */
  readonly deductions?: number;
}

/**
 * Calculate income tax for an individual / sole proprietor.
 *
 * Returns the slab-applied tax (after rebates and rounding). For full
 * payroll-style breakdown including SSF/PF/insurance deductions, use
 * `calculatePayroll` instead.
 *
 * @example
 * calculateIncomeTax({ income: 800_000, status: "single" }).tax;
 * // 5000  + 20000 + 20000 = 45000
 */
export function calculateIncomeTax(options: IncomeTaxOptions): IncomeTaxResult {
  const {
    income,
    status = "single",
    fiscalYear,
    profile = {},
    deductions = 0,
  } = options;
  ensureNonNegative(income, "calculateIncomeTax");
  ensureNonNegative(deductions, "calculateIncomeTax");

  const rates = getRates(fiscalYear);
  const fullProfile: TaxProfile = { status, ...profile };
  const notes: string[] = [];

  // Non-resident — flat rate.
  if (fullProfile.residency === "non-resident") {
    const tax = r2(Math.max(0, income - deductions) * 0.25);
    notes.push("Non-resident flat 25%");
    return {
      fiscalYear: rates.fiscalYear,
      grossIncome: income,
      taxableIncome: r2(Math.max(0, income - deductions)),
      tax,
      effectiveRate: income > 0 ? r4(tax / income) : 0,
      perBracket: [],
      notes,
    };
  }

  let slabs = status === "couple" ? rates.slabsCouple : rates.slabsSingle;

  // SSF members are exempt from the 1% SST slab — replace with 0%.
  if (fullProfile.isSSFMember) {
    slabs = slabs.map((s, i) => (i === 0 ? { ...s, rate: 0, label: "Exempt (SSF)" } : s));
    notes.push("SSF member: 1% SST exempt");
  }

  // Apply remote-area deduction.
  let extraDeduction = 0;
  if (fullProfile.remoteArea) {
    extraDeduction = REMOTE_AREA_DEDUCTIONS[fullProfile.remoteArea];
    notes.push(`Remote area ${fullProfile.remoteArea}: -Rs ${extraDeduction.toLocaleString()}`);
  }

  // Disabled / pensioner widen the basic exemption.
  const widenedThreshold = thresholdFor(slabs, fullProfile);
  if (fullProfile.isDisabled) notes.push(`Disabled exemption: threshold widened to Rs ${widenedThreshold.toLocaleString()}`);
  if (fullProfile.isPensioner) notes.push(`Pensioner exemption: threshold widened to Rs ${widenedThreshold.toLocaleString()}`);

  const adjustedSlabs = (fullProfile.isDisabled || fullProfile.isPensioner)
    ? widenSlabs(slabs, widenedThreshold)
    : slabs;

  const taxable = Math.max(0, income - deductions - extraDeduction);
  const { tax: rawTax, perBracket } = applySlabs(taxable, adjustedSlabs);

  // Women rebate: 10% off the computed tax (employment income, single only).
  let tax = rawTax;
  if (fullProfile.isWomanRebate && status === "single") {
    tax = r2(rawTax * 0.9);
    notes.push("Women rebate: 10% reduction");
  }

  return {
    fiscalYear: rates.fiscalYear,
    grossIncome: income,
    taxableIncome: r2(taxable),
    tax,
    effectiveRate: income > 0 ? r4(tax / income) : 0,
    perBracket,
    notes,
  };
}

/** Internal: shift the basic exemption to a wider threshold. */
function widenSlabs(slabs: SlabSet, newFirstTo: number): SlabSet {
  const original = slabs[0];
  if (!original) return slabs;
  if (newFirstTo <= original.to) return slabs;
  const widened: TaxSlab[] = [{ ...original, to: newFirstTo }];
  for (let i = 1; i < slabs.length; i++) {
    const s = slabs[i]!;
    if (s.from < newFirstTo) {
      if (s.to > newFirstTo) {
        widened.push({ ...s, from: newFirstTo });
      }
      // else fully absorbed — drop
    } else {
      widened.push(s);
    }
  }
  return widened;
}

/** Quick helper: get the slab table for a status & FY. */
export function getSlabs(
  status: TaxProfile["status"] = "single",
  fiscalYear?: FiscalYear,
): SlabSet {
  const rates = getRates(fiscalYear);
  return status === "couple" ? rates.slabsCouple : rates.slabsSingle;
}

/** Effective tax rate (= tax / income) for a given income. */
export function getEffectiveRate(
  income: number,
  status: TaxProfile["status"] = "single",
  fiscalYear?: FiscalYear,
): number {
  if (income <= 0) return 0;
  const result = calculateIncomeTax({ income, status, fiscalYear });
  return result.effectiveRate;
}

/** Marginal rate at a given income level. */
export function getMarginalRate(
  income: number,
  status: TaxProfile["status"] = "single",
  fiscalYear?: FiscalYear,
): number {
  const slabs = getSlabs(status, fiscalYear);
  for (const s of slabs) {
    if (income > s.from && income <= s.to) return s.rate;
  }
  // Above all brackets — return top rate.
  return slabs[slabs.length - 1]?.rate ?? 0;
}
